import type { IntradayExitAction, IntradayKind } from "@atc/domain";
import type {
  BacktestReportWire,
  DossierWire,
  QualificationFieldWire,
  QualificationReportWire,
} from "@atc/evals";
/**
 * The only place the terminal talks to the server.
 *
 * Every command maps to an API call and nothing is computed here that the API
 * does not return. That rule is what keeps the vertical slice honest: the
 * moment the UI holds logic, it stops proving the API can serve a real
 * consumer — and the Alpaca-compatible surface in M3 would quietly diverge from
 * what this shows.
 */

/**
 * `error` is the machine-readable slug; `reason` is the sentence the API wrote
 * for a human, when it wrote one. Commands should prefer `reason` in the line
 * they print and keep `error` for branching — a user reading `email_not_allowed`
 * has to guess, and the server already knows the answer. See M2/E8.
 */
export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; error: string; reason?: string; code?: string };

/** Same-origin in production; the Vite dev server proxies /api to the Worker. */
const BASE = "";

async function call<T>(path: string, init?: RequestInit): Promise<ApiResult<T>> {
  try {
    const res = await fetch(`${BASE}${path}`, {
      ...init,
      // The session is an HttpOnly cookie. The terminal never sees the token,
      // which is why a stolen page context cannot exfiltrate it.
      credentials: "include",
      headers: {
        ...(init?.body ? { "content-type": "application/json" } : {}),
        ...init?.headers,
      },
    });

    const text = await res.text();
    const body = text ? JSON.parse(text) : {};

    if (!res.ok) {
      // A server failure names its request (M7/I6), and the terminal says it
      // under whatever the command prints, so a trader has one string to quote
      // and the operator one string to find every log line and trace with.
      // Only on a 5xx: a refusal is the trader's to read, not ours to chase.
      const requestId = res.headers.get("x-request-id") ?? body.request_id;
      const cite = (line: string) =>
        res.status >= 500 && typeof requestId === "string"
          ? `${line} — request ${requestId}`
          : line;
      return {
        ok: false,
        status: res.status,
        error: cite(body.message ?? body.error ?? `HTTP ${res.status}`),
        ...(typeof (body.code ?? body.error) === "string" ? { code: body.code ?? body.error } : {}),
        ...(typeof body.reason === "string" ? { reason: cite(body.reason) } : {}),
      };
    }
    return { ok: true, data: body as T };
  } catch (err) {
    return { ok: false, status: 0, error: err instanceof Error ? err.message : "network error" };
  }
}

/** `GET /api/v1/accounts/:id/deletion` (M7/I12). */
export type AccountDeletion = {
  id: string;
  name: string;
  status: string;
  closed_at: string | null;
  open_orders: number;
  positions: number;
  enabled_strategies: number;
  enabled_schedules: Array<{ kind: string; symbol: string }>;
  mirror_connected: boolean;
  sync_every_minutes: number | null;
  fills: number;
  execution_probes: number;
};

/** `GET /api/v1/recorder` — the trader's own quote recorder (M7/E19). */
export type RecorderStatus = {
  enabled: boolean;
  symbols: string[];
  next_tick_at: string | null;
  last: { at: string; code: string; printed: number; unknown: string[] } | null;
  printed: number;
};

/** `GET /api/v1/recorder/tape` — one UTC day, in `record-session.mjs`'s artifact format. */
export type RecordedTape = {
  schema: 1;
  kind: "intraday-quotes";
  date: string;
  quotes: { symbol: string; as_of: string; [field: string]: string }[];
};

export type Quote = {
  symbol: string;
  /** Which venue. Both markets list AAPL, so the symbol alone is not enough. */
  market: string;
  /** Whether that venue was open when the price was TAKEN, not now. */
  market_state: string;
  bid: string;
  ask: string;
  last: string;
  as_of: string;
  /** Which feed priced it — `alpaca`, `synthetic`. Named since `M4/E30`. */
  source: string;
  /** What sort of prices those are — `live`, `simulated`, `recorded`. */
  kind: string;
};

/**
 * One market, exactly as `/api/v1/markets` describes it.
 *
 * `opens_at`, `closes_at` and `reason` are present only when they mean
 * something, so the terminal prints what it is given rather than deciding which
 * nulls to hide.
 */
export type MarketSummary = {
  id: string;
  name: string;
  currency: string;
  time_zone: string;
  state: "open" | "closed" | "unknown";
  local_date: string;
  opens_at?: string;
  closes_at?: string;
  reason?: string;
  calendar_through: string;
  /**
   * WHO prices this venue for you — `alpaca`, `synthetic` — or null if nothing
   * can. Published since `M4/E30`: a trader priced from a key they connected
   * themselves is entitled to see whose feed answered.
   */
  priced_by: string | null;
  /**
   * And what sort of prices those are — `live`, `simulated`, `recorded`, or
   * null alongside a null `priced_by`. The pair travels together so a reader is
   * never left guessing which of the two was the vendor.
   */
  priced_by_kind: string | null;
  /**
   * How far behind the prices are, in minutes.
   *
   * The server's own figure, taken from the policy that decides whether a
   * resting order may trigger against a price. The terminal prints it and never
   * carries a number of its own: a delay hardcoded in a browser is a promise
   * that goes quietly wrong the day the feed's entitlement changes.
   */
  quote_delay_minutes: number;
  /** Priced in real time (`ADR-0039`): the header says so rather than a delay. */
  realtime?: boolean;
};

export type MarketsResponse = { at: string; markets: MarketSummary[] };

/** One instant of a replayed day, exactly as the server describes it. */
export type ReplayFrame = {
  date: string;
  source: string;
  kind: string;
  timeframe: string;
  symbols: string[];
  speed: number;
  /** The RECORDED instant being shown — never the wall clock. */
  at: string;
  session: { start: string; end: string };
  progress: number;
  finished: boolean;
  /** Bars carry no bid/ask; this is the model the spread came from. */
  modeled_half_spread_bps: Record<string, number>;
  quotes: Quote[];
};

export type Account = {
  id: string;
  name: string;
  mode: string;
  status: string;
  base_currency: string;
  /** What the account can spend now, from its Durable Object. */
  cash: string;
  equity: string;
  market_value?: string;
  positions?: number;
  /** `sum(ledger_entries)` — the durable figure, which the queue catches up to. */
  ledger_cash?: string;
  ledger_entries: number;
};

export type Order = {
  id: string;
  client_order_id: string;
  symbol: string;
  side: "buy" | "sell";
  qty: string;
  type: string;
  time_in_force: string;
  limit_price: string | null;
  stop_price: string | null;
  status: string;
  filled_qty: string;
  avg_fill_price: string | null;
  reject_reason: string | null;
  stop_fired: boolean;
  /** `user`, `agent` or `system` — what kind of thing placed it. `M5/E10`. */
  submitted_by: string;
  /** Which run, when an agent placed it. `null` otherwise. */
  agent_run_id: string | null;
  submitted_at: string;
  expires_at: string | null;
  canceled_at: string | null;
  replaced_by_order_id: string | null;
  /**
   * The same order on the account's Alpaca paper account (`M7/I11`), or null
   * when it was never mirrored. Absent from a server older than M7/I11.
   */
  mirror?: OrderMirror | null;
  /**
   * The same order on the operator's LIVE Alpaca account (`ADR-0050`), or null
   * when live mirroring was off when it was placed. Absent from an older server.
   */
  live_mirror?: OrderMirror | null;
};

/** `GET /api/v1/accounts/:id/live-mirror` — never the secret. */
export type LiveMirror = {
  key_id: string;
  account_number: string;
  status: string;
  enabled: boolean;
  verified_at: string;
  connected_at: string;
  linked_by: string;
};

/** `pending`: on its way · `open`: resting at Alpaca · `decided`: `code` says how it ended. */
export type OrderMirror = {
  state: "pending" | "open" | "decided";
  code: string | null;
  detail: string | null;
  venue_order_id: string | null;
  status: string | null;
  filled_qty: string | null;
  fill_price: string | null;
};

/** `GET /api/v1/accounts/:id/gap` — every order on the account beside its mirror (`M7/I11`). */
export type AccountGap = {
  account_id: string;
  date: string;
  pending_mirrors: number | null;
  orders: {
    order_id: string;
    client_order_id: string;
    submitted_at: string;
    symbol: string;
    side: "buy" | "sell";
    type: string;
    time_in_force: string;
    submitted_by: string;
    planned: { status: string; qty: string; fill: string | null };
    mirror: null | {
      code: string;
      status: string | null;
      venue_order_id: string | null;
      time_in_force: string | null;
      qty: string;
      fill: string | null;
      filled_at: string | null;
    };
    execution_gap_bps: string | null;
    quantity_gap: string | null;
  }[];
  session: { orders: number; mirrored: number; unmirrored: number; filled_real: number };
};

/** What a resting order is holding, so the terminal never has to infer it. */
export type PositionsResponse = {
  market_id: "US" | "SIM";
  base_currency: string;
  positions: Position[];
  market_value: string;
  cash: string;
  available_cash: string;
  reserved_cash: string;
  equity: string;
  open_orders: number;
};

export type Fill = { id: string; qty: string; price: string; fee: string; filled_at: string };

export type Position = {
  symbol: string;
  qty: string;
  cost_basis: string;
  average_price: string;
  reserved_qty: string;
  available_qty: string;
  mark?: string;
  market_value?: string;
};

export type WhoAmI = {
  user_id: string;
  email: string;
  authenticated: boolean;
  /**
   * Whether this session may do anything yet.
   *
   * It is not what enforces the terms — every other route refuses on its own
   * (`403 agreement_required`) regardless of what the terminal does with this.
   * It is how the terminal knows to ask before the user runs into a refusal.
   */
  agreement_required: boolean;
  agreement_version: string;
};

/** The terms of use, exactly as the server words them. */
export type Agreement = { version: string; updated_at: string; lines: string[] };

/**
 * One feed a trader may be priced by, as `GET /api/v1/feeds` returns it.
 *
 * `because` and `kind` both travel because they answer different questions:
 * *why may I use this* and *are these real prices*. A picker without the first
 * cannot explain itself, and one without the second is offering `alpaca` and
 * `synthetic` as if the choice between them were a matter of taste.
 */
export type Feed = {
  provider: string;
  /**
   * `"your_plan"` is back, and it is not the value that went away.
   *
   * Until `M4/I13/T5` it meant the deployment's own licence lent to a trader
   * who had brought none. `M4/I14` reinstated it for a feed the platform is
   * **licensed to redistribute**, included in this account's plan. Nothing
   * grants it today — no vendor we can buy from declares the right — so the
   * terminal will not see one until a licence is actually bought.
   *
   * It carries no `id`, `key_id` or `verified_at`: there is no key behind it,
   * and nothing for the trader to connect or revoke.
   */
  because: "everyone" | "your_key" | "your_plan";
  /** `live`, `simulated`, `recorded` — or null for an id this build cannot name. */
  kind: string | null;
  selected: boolean;
  /** The connection id, when this rests on a key. What `disconnect` takes. */
  id?: string;
  key_id?: string;
  verified_at?: string;
};

export type Feeds = {
  /** The account this feed prices — the selected one unless asked otherwise (`M7/E33`). */
  account_id: string | null;
  selected: string;
  /** Whether they chose it, or are simply on the default. Drives the tour step. */
  chosen: boolean;
  /** What they had chosen, when it stopped being granted. */
  lapsed?: string;
  /** Priced in real time: the trader's own key's plan includes it (`ADR-0039`). */
  realtime?: boolean;
  feeds: Feed[];
  accepts_keys: string[];
};

/** An account's mirror key, as `GET /api/v1/accounts/:id/mirror` shows it. Never the secret. */
export type MirrorConnection = {
  provider: string;
  /** Which Alpaca account the selected account trades through (`ADR-0059`). */
  trading_account: "paper" | "live";
  linked_by: string | null;
  key_id: string;
  account_number: string | null;
  verified_at: string;
  /** `sync every HH:MM`, or null (`M7/I10`). */
  sync_every?: string | null;
  next_sync_at?: string | null;
};

/** What `POST /api/v1/accounts/:id/sync` appended, as the server planned it (`ADR-0037`). */
export type SyncPlan = {
  fills: { symbol: string; side: string; role: "close" | "open"; qty: string; price: string }[];
  cash_transfer: string;
  canceled: string[];
  kept: string[];
  created: {
    client_order_id: string;
    symbol: string;
    side: string;
    type: string;
    time_in_force: string;
    qty: string;
    limit_price: string | null;
    stop_price: string | null;
  }[];
  not_recreated: { client_order_id: string; symbol: string; reason: string }[];
  residue: { symbol: string; broker: string; ours: string; difference: string }[];
  /** How far below zero the broker's margin cash was; ours stops at 0 (`ADR-0059`). */
  cash_shortfall?: string;
  /** Broker quantities finer than our 8 places, and what was kept (`M8/E24`). */
  qty_dust?: { symbol: string; broker: string; kept: string }[];
  target: { cash: string; positions: { symbol: string; qty: string; cost_basis: string }[] };
};

export type SyncResult = {
  status: "applied" | "no_change" | "dry_run";
  sync_id: string;
  idempotent: boolean;
  plan: SyncPlan;
  equity_before: string;
  equity_after: string;
  flow: string;
  flow_priced: boolean;
  recorded: boolean;
};

/** One row of `GET /api/v1/accounts/:id/syncs`. */
export type SyncRecord = {
  id: string;
  trigger: "manual" | "scheduled";
  code: string;
  detail: string | null;
  account_number: string | null;
  snapshot_as_of: string | null;
  flow: string | null;
  flow_priced: boolean | null;
  summary: {
    fills: number;
    cash_transfer: string;
    canceled: number;
    created: number;
    not_recreated: number;
  } | null;
  created_at: string;
  plan?: SyncPlan | null;
};

/** What `POST /api/v1/feeds/select` ended on the account it switched (`M7/E33`). */
export type FeedSwitch = {
  canceled_orders: string[];
  stopped: { kind: string; symbol?: string }[];
  /** Steps the server could not finish; empty normally. */
  incomplete: string[];
};

/**
 * The limits an order is checked against, as `GET /api/v1/policy` returns them.
 *
 * Money is a string here for the same reason it is everywhere else on this
 * wire: a JSON number is a float, and a limit compared against values that are
 * scaled integers must not have been through one.
 */
export type Policy = {
  kill_switch: boolean;
  max_notional_per_order: string;
  max_intraday_notional_per_order: string;
  max_position_notional: string;
  max_orders_per_day: number;
  symbol_allowlist: string[];
  symbol_denylist: string[];
  agents_may_trade: boolean;
};

/** What one account is actually enforcing, which can lag what was set. */
export type PolicyEnforcement = {
  account_id: string;
  source: string;
  orders_today: number | null;
  policy: Policy | null;
};

export type PolicyState = { policy: Policy; enforcing: PolicyEnforcement[] };

export type KillSwitch = {
  on: boolean;
  changedBy: string | null;
  changedAt: string | null;
  reason: string | null;
};

/**
 * One agent run, exactly as the server reports it.
 *
 * **`effects_applied` and `effects_blocked` keep the runtime's names**, and the
 * terminal renders them as orders or as insights depending on the domain rather
 * than renaming the fields. The columns hold the runtime's facts and "orders
 * placed" is one domain's reading of them — the insight pack shares the table
 * and places nothing.
 */
export type AgentRun = {
  id: string;
  domain: string | null;
  agent_key: string;
  agent_version: string;
  subject_ref: string | null;
  trigger: string;
  status:
    | "queued"
    | "running"
    | "awaiting_approval"
    | "succeeded"
    | "failed"
    | "blocked"
    | "cancelled";
  model: string | null;
  input_tokens: number;
  cached_input_tokens: number;
  output_tokens: number;
  cost_usd: string;
  effects_applied: number;
  effects_blocked: number;
  outcomes: AgentRunOutcome[];
  /** The question this run stopped to ask, while it is unanswered. */
  approval: AgentApproval | null;
  /** A sentence a person is meant to read and act on. Printed verbatim. */
  blocked_reason: string | null;
  /** A diagnostic. Different field because it is a different audience. */
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
};

/**
 * What a waiting run is waiting for.
 *
 * `question` is a sentence the server wrote, printed verbatim — the terminal
 * never assembles one from `detail`, which is the same rule `blocked_reason`
 * follows and for the same reason: reformatting a server's sentence means
 * parsing it.
 */
export type AgentApproval = {
  code: string;
  question: string;
  detail?: Readonly<Record<string, string | number | boolean | null | readonly string[]>>;
};

export type AgentRunOutcome = {
  name: string;
  impact: "material" | "none";
  status: "applied" | "blocked";
  ref?: string;
  idempotent?: boolean;
  code?: string;
  reason?: string;
  detail?: Readonly<Record<string, string | number | boolean | null | readonly string[]>>;
};

export type AgentInsight = {
  id: string;
  kind: string;
  severity: number;
  title: string;
  body_md: string;
};

export type AgentRunDetail = {
  run: AgentRun;
  /**
   * The Durable Object's own view, which may be `null` or stale.
   *
   * The row is the authority. A run that finished with the object evicted has a
   * complete row and a stale state, so a poller that waited on this would wait
   * for ever — `agent status` polls `run.status`.
   */
  state: { lastStep?: string; lastStatus?: string; revision?: number } | null;
  insights: AgentInsight[];
};

export const AGENT_TERMINAL_STATUSES = ["succeeded", "failed", "blocked", "cancelled"] as const;

/**
 * The day's model spend, exactly as the server reports it.
 *
 * **`spent_today_usd` is the ceiling's own total, not a sum of the run rows**,
 * so it can exceed what `runs_today` runs appear to account for while runs are
 * in flight. The terminal prints both rather than reconciling them — a client
 * that computed one from the other would be inventing a number the API does not
 * expose, which is the rule this whole surface is built on.
 */
export type AgentSpend = {
  day: string;
  spent_today_usd: string;
  cap_usd: string;
  remaining_usd: string;
  per_run_cap_usd: string;
  runs_today: number;
  platform_spent_today_usd: string;
};

/**
 * A strategy's score, exactly as the server computes it.
 *
 * **Every statistic is optional and that is the contract, not laziness.** A
 * field the evidence cannot support is absent rather than zero — two
 * observations cannot produce a Sortino, and rendering `0` there would be a
 * confident claim about risk. The terminal prints "—" for an absent figure and
 * never substitutes one.
 */
export type AgentEvals = {
  account_id: string;
  window_days: number;
  points: number;
  from?: string;
  to?: string;
  total_return?: number;
  benchmark_return?: number;
  excess_return?: number;
  max_drawdown: number;
  sortino?: number;
  turnover?: number;
  cost_usd: string;
  cost_per_point_of_return?: number;
  feeds: string[];
  variants: string[];
  runs: number;
  refusals: string[];
  caveats: string[];
};

export type AgentPerformance = {
  account_id: string;
  window_started_at: string | null;
  window_ended_at: string | null;
  samples: number;
  feeds: string[];
  pnl_usd?: string;
  buy_and_hold_pnl_usd?: string;
  excess_pnl_usd?: string;
  beat_buy_and_hold?: boolean;
  observed_strategies: { agent_key: string; versions: string[] }[];
  attribution:
    | { status: "unique"; agent_key: string; versions: string[] }
    | { status: "unattributed" };
  policy_blocks: number;
  cost_usd: string;
  recent_runs_limit: number;
  recent_runs: {
    id: string;
    agent_key: string;
    agent_version: string;
    status: string;
    policy_blocks: number;
    cost_usd: string | null;
    outcomes: AgentRunOutcome[];
    started_at: string | null;
    finished_at: string | null;
  }[];
  caveats: string[];
};

/**
 * A strategy scored against years of adjusted history, deterministically.
 *
 * The counterpart to `AgentEvals` and deliberately a different shape: that one
 * is a FORWARD score on one trader's account against their own live feed, this
 * one is a property of the strategy itself. Merging them would put an
 * `account_id` on a figure that belongs to nobody's money.
 */
/**
 * One description, imported rather than repeated. `M6/E1`.
 *
 * This was a hand-written copy of the Worker's `BacktestReport` and nothing
 * compared the two — the failure mode that once printed every figure on this
 * screen as a dash, deployed, for as long as the endpoint had existed. The
 * shape lives in `@atc/evals`, which is pure and safe to bundle here.
 */
export type AgentBacktest = BacktestReportWire;

export type { IntradayKind };
export type IntradayConfig = {
  enabled: boolean;
  symbol: string;
  kind: IntradayKind;
  notional: string;
  tranche: string;
  core: string;
  max_position: string;
  band_bps: number;
  lookback: number;
  interval_minutes: number;
  max_spread_bps?: number;
  /** DCA only (M8/E4): null or absent is off. */
  take_profit_pct?: string | null;
  take_profit_usd?: string | null;
  stop_loss_pct?: string | null;
  stop_loss_usd?: string | null;
  exit_action?: IntradayExitAction;
  /** Shares per buy — a `shares` schedule (M8/I10). */
  qty?: string;
  /** The buying options of dca and shares (M8/I11). */
  below_bps?: number | null;
  flat_weekend?: boolean;
  cooldown_minutes?: number | null;
};
/** The last take profit or stop loss a schedule hit, and whether it stopped for it (M8/E4). */
export type IntradayExit = {
  reason: "take_profit" | "stop_loss" | "flat_weekend";
  at: string;
  probe_id: string;
  stopped: boolean;
};
/** A DCA exit resting at Alpaca on a mirrored account (M8/I9); null when the engine checks it. */
export type ExitAtBroker = {
  resting: {
    venue_order_id: string;
    qty: string;
    take_profit: string | null;
    stop_loss: string | null;
    placed_at: string;
  } | null;
  /** The fraction's own day order (M8/E6). */
  fraction?: {
    venue_order_id: string;
    qty: string;
    kind: "limit" | "stop";
    price: string;
    placed_at: string;
  } | null;
  note: string | null;
  error: string | null;
  finishing: boolean;
  /** Its copies on your LIVE account (M8/I13). Absent from an older server. */
  live?: {
    resting: LiveExitCopy | null;
    fraction: LiveExitCopy | null;
    rest: LiveExitCopy | null;
    kept: LiveExitCopy[];
    error: string | null;
  } | null;
};
export type LiveExitCopy = {
  client_order_id: string;
  venue_order_id: string | null;
  kind: "oco" | "limit" | "stop" | "market";
  qty: string;
  take_profit: string | null;
  stop_loss: string | null;
  price: string | null;
  status: string | null;
  note: string | null;
};
/** Which price a schedule's ticks decide on — its account's feed (M7/E44). */
export type DecidesOn = "sip_realtime" | "delayed_sip";

export type IntradayOptions = {
  notional?: string;
  tranche?: string;
  core?: string;
  max_position?: string;
  band_bps?: number;
  lookback?: number;
  interval_minutes?: number;
  max_spread_bps?: number;
  take_profit_pct?: string;
  take_profit_usd?: string;
  stop_loss_pct?: string;
  stop_loss_usd?: string;
  exit_action?: IntradayExitAction;
  qty?: string;
  below_bps?: number;
  flat_weekend?: boolean;
  cooldown_minutes?: number;
};

/** A rebalance setting's book (`M10/I5`): the shares it owns. */
export type RebalanceBookView = { symbol: string; qty: string; average_price: string | null }[];

/**
 * Where one setting of an account's rebalance lives (`M10/I5`): `default` at the account's own
 * path, as before, any other under `/settings/<name>`.
 */
const settingPath = (accountId: string, name?: string) =>
  `/api/v1/rebalance/${encodeURIComponent(accountId)}${
    name === undefined || name === "default" ? "" : `/settings/${encodeURIComponent(name)}`
  }`;

/** One side of `rebalance compare` (`M9/I4`). */
export type RebalanceCompareSide = {
  account_id: string;
  /** Absent from a server before M10/I5. */
  name?: string;
  config: RebalanceConfigView;
  enabled: boolean;
  record: {
    since: string;
    points: number;
    return: number | null;
    equal_weight_return: number | null;
    spy_return: number | null;
  } | null;
  ladder: { counters: LadderCountersView; error: string | null } | null;
};

/** A rebalance's stored configuration, as the API shows it (`M9/I2`). */
export type RebalanceConfigView = {
  top: number;
  lookback: number;
  score: string;
  weight: string;
  every: string;
  at: string;
  /** Absent from a server before M10/I6; null means `at`'s moment. */
  sell_at?: string | null;
  buy_at?: string | null;
  resize_band_pct: number;
  stop_pct: number | null;
  /** Absent from a server before M9/E3. */
  session?: number;
  universe: string;
  /** Absent from a server before M9/E4; empty for none. */
  exclude?: string[];
  capital: number | null;
  /** Absent from a server before M9/I4, null without one. */
  ladder?: RebalanceLadderConfigView | null;
  enabled: boolean;
  /** Absent from a server before M10/I5. */
  name?: string;
  share?: number;
  /** `--when` and `--then` as the flags read them back (`M10/I9`); null without, absent before. */
  when?: string | null;
  then_flags?: string | null;
} & RebalanceFiltersView;
/** The entry filters (`M9/I7`): absent from an older server, null when off. */
export type RebalanceFiltersView = {
  skip_spike_pct?: number | null;
  market?: string | null;
  market_action?: string | null;
};
export type RebalanceLadderConfigView = {
  tp_pct: number | null;
  tp_usd: number | null;
  rebuy_under_pct: number;
  rebuy_above_pct: number | null;
  /** `"off"` or the scale's word (`M10/I4`); absent from an older server. */
  scale?: string;
};
export type LadderCountersView = {
  take_profits: number;
  dip_rebuys: number;
  breakout_rebuys: number;
  stub_minutes: number;
};
/** Where a rebalance's ladder stands (`M9/I4`). */
export type RebalanceLadderView = {
  next: string | null;
  error: string | null;
  market: string | null;
  counters: LadderCountersView;
  /** Each symbol's k and where it came from (`M10/I4`); null without a scale. */
  scales?: {
    at: string;
    spec: string;
    median: number | null;
    by_symbol: Record<string, number>;
    unread: string[];
  } | null;
  /** Whether this week steps on `--then`'s ladder (`M10/I9`); null before a rebalance reads it. */
  when?: { on: boolean; at: string; decision: string } | null;
  positions: {
    symbol: string;
    mode: "full" | "stub";
    /** The position's k, its levels already scaled; null without a scale. */
    k?: number | null;
    reference: number | null;
    take_profit_at: number | null;
    take_profit_price: number | null;
    rebuy_under_at: number | null;
    rebuy_above_at: number | null;
    bucket: number;
    since: string;
  }[];
  log: {
    at: string;
    symbol: string;
    kind: string;
    side: "buy" | "sell";
    qty: string;
    mid: string;
    status: string;
    code: string | null;
    price: string | null;
  }[];
};
export type RebalanceIntentView = {
  action: "buy" | "sell";
  symbol: string;
  qty: string;
  status: string;
  code: string | null;
  price: string | null;
  client_order_id: string;
};
export type RebalanceDecisionView = {
  id: string;
  at: string;
  kind: "rebalance" | "stop" | "liquidate";
  dry_run: boolean;
  picks: { symbol: string; score: number; weight: number }[];
  unscored: string[];
  /** Absent from a decision logged before M9/I7. */
  skipped?: string[];
  market?: {
    on: string;
    sma: number;
    risk_off: boolean;
    below: string[];
    unread: string[];
  } | null;
  history: { through: string; failed: string[]; note: string | null };
  budget: { cash: string; capital: number | null };
  intents: RebalanceIntentView[];
  error: string | null;
  /** Which side of a rebalance split in time (`M10/I6`); absent when it did both at once. */
  leg?: "sell" | "buy";
  /** Each pick's ladder k (`M10/I4`); null or absent without a scale. */
  ladder_scale?: {
    spec: string;
    median: number | null;
    by_symbol: Record<string, number>;
    unread: string[];
  } | null;
  /** What `--when` read (`M10/I9`); null at a stop check or without one, absent before. */
  when?: {
    spec: string;
    pick: string | null;
    value: number | null;
    threshold: number | null;
    samples: number;
    on: boolean;
    reason: "pick_unmeasured" | "measure_unread" | "too_few_samples" | null;
  } | null;
};
export type RebalanceNext = { at: string; rebalance: boolean; leg?: "buy" } | null;

/** `GET /api/v1/rebalance/backtest` (`M9/I1`). */
export type RebalanceStatsWire = {
  from: string;
  to: string;
  total_return: number;
  cagr: number;
  max_drawdown: number;
  by_year: { year: string; return: number }[];
};
export type RebalanceBacktest = {
  config: {
    top: number;
    lookback: number;
    score: string;
    weight: string;
    every: string;
    at: string;
    resize_band_pct: number;
    invest_pct?: number;
    stop_pct: number | null;
    /** Absent from a server before M9/E3. */
    session?: number;
  } & RebalanceFiltersView;
  universe: { kind: string; symbols: number; members: string[]; exclude?: string[] };
  data: {
    source: string;
    artifact_end: string;
    through: string;
    failed: { symbol: string; reason: string }[];
  };
  cash: string;
  capital: number | null;
  cost_bps: number;
  first_trade_day: string;
  /** With `start`/`end` (M9/E5): the first and last sessions reported. */
  window?: { start: string; end: string } | null;
  strategy: RebalanceStatsWire | null;
  fit: RebalanceStatsWire | null;
  test: RebalanceStatsWire | null;
  without_hindsight_winners: (RebalanceStatsWire & { excluded: string[] }) | null;
  benchmarks: { spy: RebalanceStatsWire | null; equal_weight: RebalanceStatsWire | null };
  activity: { rebalances: number; stop_checks: number; fills: number; rejected: number };
  /** Each setting's part of a shared run (`M10/I5`); null or absent for one alone. */
  settings?:
    | {
        name: string;
        share: number;
        config: RebalanceBacktest["config"] | null;
        rebalances: number;
        strategy: RebalanceStatsWire | null;
      }[]
    | null;
  code_digest: string;
  notes: string[];
  equity: [string, number][];
  cash_flow: {
    from: string;
    first_trade_day: string;
    opening_cash: string;
    end_balance: number | null;
    return: number | null;
    max_drawdown: number | null;
    weekly: { week_ending: string; balance: number; cash: number }[];
    spy_weekly: { week_ending: string; balance: number }[];
  } | null;
};

/** `rebalance tune` (M9/E6, ADR-0056): a grid's progress, then its report. */
export type TuneStatsWire = {
  cagr: number;
  total_return: number;
  max_drawdown: number;
  score: number;
};
export type TuneRowWire = {
  setting: { top: number; lookback: number; score: string; weight: string };
  analysis: TuneStatsWire | null;
  test: TuneStatsWire | null;
  analysis_rank: number;
  test_rank: number | null;
};
/** What a tune held fixed for its whole grid; older records carry only `every` and `at`. */
export type RebalanceTuneRequest = {
  base: {
    every: string;
    at: string;
    session?: number;
    stopPct?: number;
    resizeBandPct?: number;
    investPct?: number;
    skipSpikePct?: number;
    market?: { on: string; sma: number };
    marketAction?: string;
  };
  universe: string;
  exclude: string[];
  cash: string;
  capital?: number | null;
  cost_bps?: number;
  start?: string | null;
  end?: string | null;
  by: string;
};
export type TuneRecordWire = {
  id: string;
  status: "running" | "complete" | "failed";
  started_at: string;
  finished_at: string | null;
  total: number;
  done: number;
  error: string | null;
  request: RebalanceTuneRequest;
  report: {
    by: string;
    periods: {
      window: { start: string; end: string };
      split: string;
      analysis: { start: string; end: string };
      test: { start: string; end: string };
    };
    rows: TuneRowWire[];
    winner: TuneRowWire | null;
    spy: { analysis: TuneStatsWire | null; test: TuneStatsWire | null };
    data: { through: string; artifact_end: string; failed: number; note: string | null };
    notes: string[];
  } | null;
};

/** `GET /api/v1/accounts/:id/valuations` — the recorded curve, oldest first. */
export type AccountValuations = {
  account_id: string;
  points: {
    as_of: string;
    equity: string;
    cash: string;
    market_value: string;
    benchmark_value: string | null;
    feed: string;
    run_moment: "start" | "end" | null;
  }[];
  next_cursor: string | null;
};

/** `GET /api/v1/intraday/:kind/:symbol/gap` — planned against real, as the server computed it. */
export type IntradayGap = {
  account_id: string;
  kind: IntradayKind;
  symbol: string;
  date: string;
  mark: { price: string; as_of: string | null; source: string } | null;
  pending_mirrors: number | null;
  orders: {
    probe_id: string;
    created_at: string;
    side: "buy" | "sell";
    planned: { qty: string; fill: string; pnl: string };
    arrival: { mid: string | null; as_of: string | null };
    mirror: null | {
      code: string;
      status: string | null;
      venue_order_id: string | null;
      qty: string;
      fill: string | null;
      filled_at: string | null;
      pnl: string | null;
    };
    execution_gap_bps: string | null;
    quantity_gap: string | null;
    latency_ms: number | null;
    sip: null | {
      bid: string;
      ask: string;
      as_of: string;
      reference: string;
      planned_gap_bps: string;
      real_gap_bps: string | null;
    };
    sip_code: string | null;
  }[];
  session: {
    orders: number;
    mirrored: number;
    unmirrored: number;
    filled_real: number;
    planned_pnl: string | null;
    planned_pnl_mirrored: string | null;
    real_pnl: string | null;
    quantity_gap: string;
  };
};
/** One schedule as `GET /api/v1/intraday` lists it — identity, and its object's own answer. */
export type IntradaySchedule = {
  account_id: string;
  account: string | null;
  market: string;
  kind: IntradayKind;
  symbol: string;
  /** `unknown` means its object did not answer: read it as possibly running. */
  status: "enabled" | "disabled" | "unknown";
  error: string | null;
  pending: boolean | null;
  last_tick_at: string | null;
  config: IntradayConfig | null;
};

export type ExecutionProbe = {
  id: string;
  kind: IntradayKind;
  symbol: string;
  action: string;
  code: string;
  qty: string | null;
  arrival_mid: string | null;
  arrival_as_of: string | null;
  arrival_source?: "delayed_sip" | "sip_realtime" | null;
  feed_error?: string | null;
  fill_price: string | null;
  shortfall_bps: string | null;
  created_at: string;
  order_id: string | null;
  sip_bid?: string | null;
  sip_ask?: string | null;
  sip_as_of?: string | null;
  sip_code?: string | null;
};

export type AgentConfig = {
  id: string;
  account_id: string;
  domain: string;
  variant: string;
  strategy_version: string;
  universe: string;
  model_tier: string;
  effective_model: string;
  cadence: "daily" | "weekly";
  enabled: boolean;
  market: string | null;
  revision: number;
  schedule_status: string;
  schedule_error: string | null;
  next_run_at: string | null;
  next_valuation_at: string | null;
};

export const agentRunFinished = (run: AgentRun): boolean =>
  (AGENT_TERMINAL_STATUSES as readonly string[]).includes(run.status);

/**
 * Whether there is any point continuing to poll.
 *
 * **`awaiting_approval` is deliberately not terminal**, so a poller that waited
 * for `agentRunFinished` alone would sit through its whole timeout and then
 * report a run that is working perfectly as one that never answered. A run
 * waiting on a person has stopped, and the person it is waiting for is the one
 * watching the poll.
 */
export const agentRunSettled = (run: AgentRun): boolean =>
  agentRunFinished(run) || run.status === "awaiting_approval";

/** `GET /api/v1/status` (M7/I6). `system` is present only for an operator. */
export type StatusWire = {
  version: string | null;
  environment: string;
  time: string;
  you: {
    feed: Record<string, unknown>;
    schedules: {
      account: string | null;
      kind: string;
      symbol: string;
      status: string;
      last_tick_at: string | null;
      overdue: boolean;
      error?: string | null;
    }[];
    questions_waiting: number;
    mirror?: {
      provider: string;
      account_number: string | null;
      next_sync_at: string | null;
    } | null;
  };
  system?: {
    last_sweep_at: string | null;
    sweep_age_s: number | null;
    rules: {
      rule: string;
      subject: string;
      verdict: string;
      branch: string;
      observed: number | null;
      swept_at: string;
    }[];
    incidents_open: {
      incident_id: string;
      rule: string;
      subject: string;
      opened_at: string;
      last_event: string;
      emailed: string;
    }[];
    traffic:
      | { unavailable: string }
      | {
          minutes: number;
          requests: number;
          errors_5xx: number;
          failed_invocations: number;
          p50_ms: number;
          p99_ms: number;
          by_entrypoint: { entrypoint: string; outcome: string; count: number }[];
        };
    quote_tape: { rows_estimate: number; bytes: number };
  };
};

export type IncidentEventWire = {
  incident_id: string;
  rule: string;
  subject: string;
  event: string;
  emailed: string;
  at: string;
};

export const api = {
  whoami: () => call<WhoAmI>("/api/auth/whoami"),

  agreement: () => call<Agreement>("/api/auth/agreement"),

  acceptAgreement: () =>
    call<{ accepted_at: string; version: string }>("/api/auth/agreement", { method: "POST" }),

  sendOtp: (email: string) =>
    call<{ success: boolean }>("/api/auth/email-otp/send-verification-otp", {
      method: "POST",
      body: JSON.stringify({ email, type: "sign-in" }),
    }),

  /** Dev-only shortcut; 404s unless AUTH_DEV_EXPOSE_OTP is enabled server-side. */
  peekOtp: (email: string) =>
    call<{ otp: string }>(`/api/auth/dev/otp?email=${encodeURIComponent(email)}`),

  verifyOtp: (email: string, otp: string) =>
    call<{ token: string; user: { id: string; email: string } }>("/api/auth/sign-in/email-otp", {
      method: "POST",
      body: JSON.stringify({ email, otp }),
    }),

  signOut: () => call<unknown>("/api/auth/sign-out", { method: "POST", body: "{}" }),

  feeds: () => call<Feeds>("/api/v1/feeds"),

  policy: () => call<PolicyState>("/api/v1/policy"),

  /**
   * Change one limit, or several.
   *
   * A partial patch: the terminal sends only what was typed, so two people
   * changing different limits cannot overwrite each other, and a field this
   * client has never heard of is not blanked by a client that does not know it
   * exists.
   */
  setPolicy: (patch: Record<string, unknown>) =>
    call<{ policy: Policy }>("/api/v1/policy", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /** Operators only; everybody else gets a 403 the command prints as-is. */
  killSwitch: () => call<KillSwitch>("/api/v1/admin/kill-switch"),

  setKillSwitch: (on: boolean, reason?: string) =>
    call<KillSwitch>("/api/v1/admin/kill-switch", {
      method: "POST",
      body: JSON.stringify(reason ? { on, reason } : { on }),
    }),

  selectFeed: (provider: string) =>
    call<Feeds & FeedSwitch>("/api/v1/feeds/select", {
      method: "POST",
      body: JSON.stringify({ provider }),
    }),

  /**
   * Connect a broker key.
   *
   * The secret goes over the wire once and is never read back — no route
   * returns it, so there is nothing here to fetch it with. On success the
   * response is the whole picker state, which is what lets `feed connect` show
   * the new grant without a second call.
   */
  connectFeedKey: (provider: string, keyId: string, secret: string) =>
    call<
      Feeds & { connected: true; account: { number: string; status: string; currency: string } }
    >("/api/v1/feeds/keys", {
      method: "POST",
      body: JSON.stringify({ provider, key_id: keyId, secret }),
    }),

  /** The broker paper account one trading account mirrors its orders into (`M7/E34`). */
  mirror: (accountId: string) =>
    call<{ mirror: MirrorConnection | null }>(
      `/api/v1/accounts/${encodeURIComponent(accountId)}/mirror`,
    ),

  /** `live`: the operator's LIVE account becomes this account's one venue (`ADR-0059`). */
  connectMirrorKey: (
    accountId: string,
    provider: string,
    keyId: string,
    secret: string,
    live = false,
  ) =>
    call<{ mirror: MirrorConnection; multiplier: string | null }>(
      `/api/v1/accounts/${encodeURIComponent(accountId)}/mirror`,
      {
        method: "POST",
        body: JSON.stringify({ provider, key_id: keyId, secret, ...(live ? { live: true } : {}) }),
      },
    ),

  /** Reset the account to its connected Alpaca paper account; `preview` only plans (`ADR-0037`). */
  sync: (accountId: string, preview: boolean) =>
    call<{ sync: SyncResult }>(
      `/api/v1/accounts/${encodeURIComponent(accountId)}/sync${preview ? "?dry_run=1" : ""}`,
      { method: "POST" },
    ),

  syncs: (accountId: string, limit = 20) =>
    call<{ syncs: SyncRecord[] }>(
      `/api/v1/accounts/${encodeURIComponent(accountId)}/syncs?limit=${limit}`,
    ),

  syncShow: (accountId: string, syncId: string) =>
    call<{ sync: SyncRecord }>(
      `/api/v1/accounts/${encodeURIComponent(accountId)}/syncs/${encodeURIComponent(syncId)}`,
    ),

  /** `every` is `HH:MM`, or null to stop the schedule. */
  syncSchedule: (accountId: string, every: string | null) =>
    call<{ mirror: MirrorConnection }>(
      `/api/v1/accounts/${encodeURIComponent(accountId)}/sync/schedule`,
      every === null ? { method: "DELETE" } : { method: "PUT", body: JSON.stringify({ every }) },
    ),

  revokeMirrorKey: (accountId: string) =>
    call<{ revoked: true }>(`/api/v1/accounts/${encodeURIComponent(accountId)}/mirror`, {
      method: "DELETE",
    }),

  revokeFeedKey: (id: string) =>
    call<Feeds>(`/api/v1/feeds/keys/${encodeURIComponent(id)}`, { method: "DELETE" }),

  listAccounts: () =>
    call<{
      selected_account_id: string | null;
      accounts: Array<{
        id: string;
        name: string;
        mode: string;
        status: string;
        /** The feed this account chose. */
        feed: string;
        /** The broker this account mirrors orders into, when it has its own key (`M7/E34`). */
        mirror: string | null;
        base_currency: string;
      }>;
    }>("/api/v1/accounts"),

  openAccount: (name: string, feed?: string, initialCash?: string) =>
    call<{ id: string; name: string; feed: string; created: boolean }>("/api/v1/accounts", {
      method: "POST",
      body: JSON.stringify({
        name,
        ...(feed ? { feed } : {}),
        ...(initialCash ? { initial_cash: initialCash } : {}),
      }),
    }),

  selectAccount: (accountId: string) =>
    call<{ account_id: string }>("/api/v1/accounts/selected", {
      method: "PUT",
      body: JSON.stringify({ account_id: accountId }),
    }),
  renameAccount: (accountId: string, name: string) =>
    call<Account>(`/api/v1/accounts/${accountId}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  closeAccount: (accountId: string) =>
    call<{ closed: boolean }>(`/api/v1/accounts/${accountId}/close`, { method: "POST" }),
  /** `GET /api/v1/accounts?closed=1` — closed accounts, found only to be deleted (M7/I12). */
  listClosedAccounts: () =>
    call<{
      accounts: Array<{ id: string; name: string; feed: string; closed_at: string | null }>;
    }>("/api/v1/accounts?closed=1"),
  /** `GET /api/v1/accounts/:id/deletion` — what an erase would destroy (ADR-0038). */
  accountDeletion: (accountId: string) =>
    call<AccountDeletion>(`/api/v1/accounts/${accountId}/deletion`),
  /** `DELETE /api/v1/accounts/:id` — erase for good; the server holds both confirmations. */
  deleteAccount: (accountId: string, confirm: string, discardOpenOrders: boolean) =>
    call<{ deleted: true; name: string }>(`/api/v1/accounts/${accountId}`, {
      method: "DELETE",
      body: JSON.stringify({
        confirm,
        ...(discardOpenOrders ? { discard_open_orders: true } : {}),
      }),
    }),
  getAccount: (id: string) => call<Account>(`/api/v1/accounts/${id}`),
  grantDelegation: (accounts: "all" | string[], expires?: string) =>
    call<{ id: string; token: string; expires_at: string }>("/api/v1/delegations", {
      method: "POST",
      body: JSON.stringify({ accounts, ...(expires ? { expires } : {}) }),
    }),
  listDelegations: () =>
    call<{
      delegations: Array<{
        id: string;
        scope: string;
        expires_at: string;
        revoked_at: string | null;
        account_ids: string[];
      }>;
    }>("/api/v1/delegations"),
  revokeDelegation: (id: string) =>
    call<{ revoked: number }>(
      `/api/v1/delegations/${id === "all" ? "all" : encodeURIComponent(id)}`,
      { method: "DELETE" },
    ),

  placeOrder: (body: {
    account_id: string;
    symbol: string;
    side: "buy" | "sell";
    qty: string;
    type: "market" | "limit" | "stop" | "stop_limit";
    time_in_force?: string;
    limit_price?: string;
    stop_price?: string;
    client_order_id?: string;
  }) =>
    // `fill` is absent when the order rests instead of executing, which is the
    // whole point of M2/I3 — the status says which happened.
    call<{ order: Order; fill?: Fill; reason?: string; idempotent: boolean }>("/api/v1/orders", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  cancelOrder: (accountId: string, orderId: string) =>
    call<{ order: Order; canceled: boolean }>(
      `/api/v1/orders/${encodeURIComponent(orderId)}?account_id=${encodeURIComponent(accountId)}`,
      { method: "DELETE" },
    ),

  orders: (accountId: string, limit = 20, status?: "open") =>
    call<{ orders: Order[] }>(
      `/api/v1/orders?account_id=${encodeURIComponent(accountId)}&limit=${limit}` +
        (status ? `&status=${status}` : ""),
    ),

  positions: (accountId: string) =>
    call<PositionsResponse>(`/api/v1/positions?account_id=${encodeURIComponent(accountId)}`),

  quote: (symbol: string) => call<Quote>(`/api/market/quote/${encodeURIComponent(symbol)}`),

  quotes: (symbols: string[]) =>
    call<{ quotes: Quote[]; unknown: string[] }>(
      `/api/market/quotes?symbols=${encodeURIComponent(symbols.join(","))}`,
    ),

  markets: () => call<MarketsResponse>("/api/v1/markets"),

  market: (id: string) => call<MarketSummary>(`/api/v1/markets/${encodeURIComponent(id)}`),

  recordings: () => call<{ recordings: string[] }>("/api/v1/replay"),

  /**
   * A recorded day at a moment inside it.
   *
   * We send how long since play was pressed and how fast; every number comes
   * back derived server-side. Nothing here computes a price, a progress
   * fraction, or a recorded timestamp — that is PRODUCT.md's rule, and it is
   * the reason `elapsed_ms` is the parameter rather than `at`.
   */
  replay: (date: string, elapsedMs: number, speed: number, symbols?: string[]) =>
    call<ReplayFrame>(
      `/api/v1/replay/${encodeURIComponent(date)}` +
        `?elapsed_ms=${Math.round(elapsedMs)}&speed=${speed}` +
        (symbols?.length ? `&symbols=${encodeURIComponent(symbols.join(","))}` : ""),
    ),

  health: () => call<{ status: string; environment: string }>("/api/health"),
  status: (accountId?: string) =>
    call<StatusWire>(
      `/api/v1/status${accountId ? `?account=${encodeURIComponent(accountId)}` : ""}`,
    ),
  incidents: () => call<{ incidents: IncidentEventWire[] }>("/api/v1/admin/incidents"),

  /**
   * Start an agent. `202` with an id to poll — never the result.
   *
   * `account_id` is omitted when the trader holds exactly one paper account,
   * which is every trader today. The server decides; the terminal does not pick
   * one, because picking silently is how an order lands in the wrong account.
   */
  startAgentRun: (variant: string, accountId?: string) =>
    call<{ run_id: string; workflow_id: string; variant: string; domain: string }>(
      "/api/v1/agents/runs",
      {
        method: "POST",
        body: JSON.stringify(accountId ? { variant, account_id: accountId } : { variant }),
      },
    ),

  agentRuns: () => call<{ runs: AgentRun[] }>("/api/v1/agents/runs"),

  putIntraday: (accountId: string, kind: IntradayKind, symbol: string, options: IntradayOptions) =>
    call<{ config: IntradayConfig; decides_on?: DecidesOn | null }>(
      `/api/v1/intraday/${kind}/${encodeURIComponent(symbol)}`,
      {
        method: "PUT",
        body: JSON.stringify({ account_id: accountId, ...options }),
      },
    ),

  rebalanceStatus: (accountId: string, limit?: number, name?: string) =>
    call<{
      rebalance: {
        /** Absent from a server before M10/I5. */
        name?: string;
        share?: number;
        book?: RebalanceBookView | null;
        config: RebalanceConfigView;
        next: RebalanceNext;
        error: string | null;
        started_at: string;
        record: {
          since: string;
          points: number;
          equity_start: string | null;
          equity_now: string | null;
          return: number | null;
          equal_weight_return: number | null;
          spy_return: number | null;
          holdings: {
            symbol: string;
            weight: number;
            target: number | null;
            drift: number | null;
          }[];
        } | null;
        log: RebalanceDecisionView[];
        /** Absent from a server before M9/I4. */
        ladder?: RebalanceLadderView | null;
      } | null;
    }>(`${settingPath(accountId, name)}${limit ? `?limit=${limit}` : ""}`),

  rebalancePreview: (accountId: string, query: Record<string, string>, name?: string) =>
    call<{
      config: RebalanceConfigView;
      /** `current`: the account's own rebalance, previewed because no setting was given. */
      from?: "current" | "asked";
      decision: RebalanceDecisionView;
    }>(`${settingPath(accountId, name)}/preview?${new URLSearchParams(query)}`),

  rebalanceOn: (accountId: string, body: Record<string, string>, name?: string) =>
    call<{
      rebalance: {
        name?: string;
        share?: number;
        config: RebalanceConfigView;
        next: RebalanceNext;
      };
      /** Absent from a server before M9/I4. */
      warnings?: string[];
    }>(settingPath(accountId, name), {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  rebalanceCompare: (a: string, b: string) =>
    call<{
      since: string;
      a: RebalanceCompareSide;
      b: RebalanceCompareSide;
    }>(`/api/v1/rebalance/compare?${new URLSearchParams({ a, b })}`),

  rebalanceNow: (accountId: string, name?: string) =>
    call<{ decision: RebalanceDecisionView }>(`${settingPath(accountId, name)}/now`, {
      method: "POST",
    }),

  rebalanceOff: (accountId: string, sell: boolean, name?: string, forget = false) => {
    const query = new URLSearchParams({
      ...(sell ? { sell: "true" } : {}),
      ...(forget ? { forget: "true" } : {}),
    }).toString();
    return call<{
      rebalance: { name?: string; config: RebalanceConfigView };
      sold: RebalanceDecisionView | null;
      forgotten?: boolean;
    }>(`${settingPath(accountId, name)}${query ? `?${query}` : ""}`, { method: "DELETE" });
  },

  rebalances: () =>
    call<{
      rebalances: {
        account_id: string;
        account: string | null;
        /** Absent from a server before M10/I5, and on an unreachable account. */
        name?: string;
        share?: number;
        book?: RebalanceBookView | null;
        status: "enabled" | "disabled" | "unreachable";
        config?: RebalanceConfigView;
        next?: RebalanceNext;
        error?: string | null;
        last_decision_at?: string | null;
      }[];
    }>("/api/v1/rebalance"),

  /** `withs`: each other setting of a shared run, as its own query string (`M10/I5`). */
  rebalanceBacktest: (query: Record<string, string>, withs: readonly string[] = []) =>
    call<RebalanceBacktest>(
      `/api/v1/rebalance/backtest?${new URLSearchParams([
        ...Object.entries(query),
        ...withs.map((w): [string, string] => ["with", w]),
      ])}`,
    ),

  rebalanceTune: (body: Record<string, string>) =>
    call<{ tune: TuneRecordWire }>("/api/v1/rebalance/tune", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  rebalanceTuneStatus: (id: string) =>
    call<{ tune: TuneRecordWire }>(`/api/v1/rebalance/tune/${encodeURIComponent(id)}`),

  accountValuations: (accountId: string, days?: number) =>
    call<AccountValuations>(
      `/api/v1/accounts/${encodeURIComponent(accountId)}/valuations${days ? `?days=${days}` : ""}`,
    ),

  recorder: () => call<RecorderStatus>("/api/v1/recorder"),

  startRecorder: (symbols: string[]) =>
    call<RecorderStatus>("/api/v1/recorder", {
      method: "PUT",
      body: JSON.stringify({ symbols }),
    }),

  stopRecorder: () => call<RecorderStatus>("/api/v1/recorder", { method: "DELETE" }),

  recordedTape: (date?: string) =>
    call<RecordedTape>(`/api/v1/recorder/tape${date ? `?date=${encodeURIComponent(date)}` : ""}`),

  accountGap: (accountId: string, date?: string) =>
    call<AccountGap>(
      `/api/v1/accounts/${encodeURIComponent(accountId)}/gap${
        date ? `?date=${encodeURIComponent(date)}` : ""
      }`,
    ),

  intradayGap: (accountId: string, kind: IntradayKind, symbol: string, date?: string) =>
    call<IntradayGap>(
      `/api/v1/intraday/${kind}/${encodeURIComponent(symbol)}/gap?account_id=${encodeURIComponent(accountId)}${
        date ? `&date=${encodeURIComponent(date)}` : ""
      }`,
    ),

  disableIntraday: (accountId: string, kind: IntradayKind, symbol: string) =>
    call<{ enabled: false; last_error?: string | null }>(
      `/api/v1/intraday/${kind}/${encodeURIComponent(symbol)}?account_id=${encodeURIComponent(accountId)}`,
      {
        method: "DELETE",
      },
    ),

  intradaySchedules: () => call<{ schedules: IntradaySchedule[] }>("/api/v1/intraday"),

  intradayLog: (accountId: string, kind: IntradayKind, symbol: string) =>
    call<{
      config: IntradayConfig | null;
      probes: ExecutionProbe[];
      pending?: boolean;
      error?: string | null;
      exit?: IntradayExit | null;
      exit_at_broker?: ExitAtBroker | null;
      hold_until?: { at: string; code: string } | null;
      history_error?: string | null;
      decides_on?: DecidesOn | null;
    }>(
      `/api/v1/intraday/${kind}/${encodeURIComponent(symbol)}/log?account_id=${encodeURIComponent(accountId)}`,
    ),

  agentConfigs: (accountId?: string) =>
    call<{ configs: AgentConfig[] }>(
      `/api/v1/agents/configs${accountId ? `?account_id=${encodeURIComponent(accountId)}` : ""}`,
    ),

  putAgentConfig: (accountId: string, cadence: "daily" | "weekly") =>
    call<{ config: AgentConfig }>("/api/v1/agents/configs/momentum", {
      method: "PUT",
      body: JSON.stringify({ account_id: accountId, cadence }),
    }),

  setAgentConfigEnabled: (accountId: string, enabled: boolean) =>
    call<{ config: AgentConfig }>(
      `/api/v1/agents/configs/momentum/${enabled ? "enable" : "disable"}`,
      {
        method: "POST",
        body: JSON.stringify({ account_id: accountId }),
      },
    ),

  /** What this trader's agents have spent today, and what is left. */
  agentSpend: () => call<AgentSpend>("/api/v1/agents/spend"),

  /** How the strategy is doing, measured forward against this trader's feed. */
  agentEvals: (days?: number) =>
    call<AgentEvals>(`/api/v1/agents/evals${days ? `?days=${days}` : ""}`),

  /** Exact forward operational P&L, blocks and run cost for this account. */
  agentPerformance: (accountId?: string) =>
    call<AgentPerformance>(
      `/api/v1/agents/perf${accountId ? `?account_id=${encodeURIComponent(accountId)}` : ""}`,
    ),

  agentRun: (runId: string) =>
    call<AgentRunDetail>(`/api/v1/agents/runs/${encodeURIComponent(runId)}`),

  /** Answer a run that stopped to ask. `202` — what it does next has not happened yet. */
  answerAgentRun: (runId: string, approved: boolean, reason?: string) =>
    call<{ run_id: string; approved: boolean; status: string }>(
      `/api/v1/agents/runs/${encodeURIComponent(runId)}/${approved ? "approve" : "reject"}`,
      { method: "POST", body: JSON.stringify(reason ? { reason } : {}) },
    ),

  /** How the strategy did against history — instant, free, identical every time. */
  agentBacktest: (strategy: string) =>
    call<AgentBacktest>(`/api/v1/agents/backtest?strategy=${encodeURIComponent(strategy)}`),

  /**
   * Whether a candidate's historical evidence supports an M8 review.
   *
   * The response type is `@atc/evals`'s own, not a hand-written copy — the
   * defect `ScoreWire` exists to prevent, where the terminal declared a payload
   * by hand and rendered every figure as a dash for as long as the endpoint had
   * existed.
   */
  agentQualify: (candidate: string) =>
    call<QualificationReportWire>(
      `/api/v1/agents/qualify?strategy=${encodeURIComponent(candidate)}`,
    ),

  /** The whole field at once. Precomputed and shipped as an artifact — `ADR-0025`. */
  agentQualifyField: () => call<QualificationFieldWire>("/api/v1/agents/qualify/field"),

  /**
   * The evidence package behind one verdict — `ADR-0017`, addressed by
   * `ADR-0026`. The response type is `@atc/evals`'s own, like everything else
   * here: the terminal cannot render a field the API does not type.
   */
  strategyDossier: (candidate: string) =>
    call<DossierWire>(`/api/v1/agents/qualify/dossier?strategy=${encodeURIComponent(candidate)}`),

  /**
   * Open an M8 review for a candidate that qualifies. A write, so a POST.
   *
   * `409 not_qualified` is the ordinary answer today and is not an error to
   * hide: nothing in the field clears `ADR-0017`'s hurdles.
   */
  strategyNominate: (candidate: string) =>
    call<{
      nominated: boolean;
      already_nominated: boolean;
      candidate: string;
      version: string;
      dossier_digest: string;
      note: string;
    }>(`/api/v1/agents/qualify/nominate?strategy=${encodeURIComponent(candidate)}`, {
      method: "POST",
    }),

  /** Which sealed holdouts have been opened. Empty is the answer M6/I4 ships. */
  strategyHoldout: () =>
    call<{
      opened: number;
      entries: readonly { seal: string; candidate: string; opened_at: string }[];
      m8_review_candidates: number;
      nominated: readonly { candidate: string; dossier_digest: string }[];
    }>("/api/v1/agents/qualify/holdout"),
};
